/**
 * Labeled empty-cell placeholder for Engine Atlas map cells that exist
 * geographically but carry no region content. Per scope doc §3 "Empty
 * cells" — two interior cells receive explicit copy explaining why they
 * are empty (substrate × decision + transformation × input). Per-cell
 * copy lives at the call site in scope-doc-verbatim form; this component
 * renders whatever copy the caller passes.
 *
 * Same visual treatment for both labeled empty cells per scope doc §3
 * "Same shape as substrate × decision empty-cell label" consistency note.
 *
 * Render style: border-dashed subdued container, text-sm text-ink-muted
 * body copy, centered. No substrate dependency — renders identically at
 * empty engine state.
 *
 * Position row's three cells get no rendered content (no EmptyCellLabel
 * call) per scope doc §3 "Position row — three cells, no labels" rule;
 * title block above carries position synthesis at whole-map altitude.
 */

import type { JSX } from 'react';

type EmptyCellCoordinate = 'substrate-decision' | 'transformation-input';

export interface EmptyCellLabelProps {
  cellCoordinate: EmptyCellCoordinate;
  copy: string;
}

export default function EmptyCellLabel({
  cellCoordinate,
  copy,
}: EmptyCellLabelProps): JSX.Element {
  return (
    <section
      id={`empty-${cellCoordinate}`}
      className="border border-dashed border-border-subtle bg-surface-1 rounded-lg py-6 px-4 text-center"
    >
      <p className="text-sm text-ink-muted">{copy}</p>
      <p className="text-xs text-ink-muted mt-2 italic">{cellCoordinate}</p>
    </section>
  );
}
